//React
import React, { useState } from "react";
import PropTypes from "prop-types";
//Assets
import copyIcon from "../../assets/svgs/copy.svg";
//CSS
import "./audienceHeader.scss";

/**
 * Audience page header (ASB-4018): the band at the top of an audience page —
 * name, ID + copy, destinations status, description, and the page's actions —
 * exactly as the V2 Audience Builder renders it. Extracted from
 * AudienceBuilderV2.js so the signal and modeled audience pages can compose the
 * same header instead of copying the retired legacy layout. Not PageHeader: that
 * is the generic titled grey band (Settings, Insights); this is the audience one.
 *
 * Three renderings, chosen by what the caller passes:
 *   view    `name` with no `onNameChange`      h1 + meta row (ID, destinations, description)
 *   edit    `onNameChange` and an `id`         name input + meta row; the description is an
 *                                              inline input when `onDescriptionChange` is
 *                                              given, plain text otherwise
 *   create  `onNameChange` and no `id`         name input + description input, no meta row
 *
 * Destinations status ("Pushed to N destinations" / "Not Yet Pushed" + Configure)
 * is part of the contract, not V2-only: the modeled page manages destinations
 * too. V2-only behaviour (the AI-explorer back link, the compact sticky state)
 * stays behind `leading` and `compact`; page actions go in `actions` and `menu`.
 */
function AudienceHeader({
	name,
	onNameChange,
	namePlaceholder,
	nameError,
	description,
	onDescriptionChange,
	descriptionPlaceholder,
	id,
	onCopyId,
	destinationsCount,
	onConfigureDestinations,
	menu,
	error,
	actions,
	leading,
	compact,
	className,
}) {
	const [menuOpen, setMenuOpen] = useState(false);
	const editing = typeof onNameChange === "function";
	const hasMeta = id != null;
	const descriptionEditable = typeof onDescriptionChange === "function";
	const namePh = namePlaceholder ?? "Audience Name";
	const descriptionPh = descriptionPlaceholder ?? "Add Description (Optional)";

	const copyId = () => {
		if (onCopyId) onCopyId(id);
		else if (navigator.clipboard) navigator.clipboard.writeText(String(id));
	};

	const destinationsItem =
		destinationsCount == null ? null : destinationsCount > 0 ? (
			<>
				<span className="as-audience-header__meta-label">Pushed to</span>
				{onConfigureDestinations ? (
					<button
						type="button"
						className="as-audience-header__link"
						onClick={onConfigureDestinations}
					>
						{destinationsCount} destination{destinationsCount !== 1 ? "s" : ""}
					</button>
				) : (
					<span className="as-audience-header__meta-value">
						{destinationsCount} destination{destinationsCount !== 1 ? "s" : ""}
					</span>
				)}
			</>
		) : (
			<>
				<span className="as-audience-header__meta-label">Not Yet Pushed</span>
				{onConfigureDestinations ? (
					<button
						type="button"
						className="as-audience-header__link"
						onClick={onConfigureDestinations}
					>
						Configure
					</button>
				) : null}
			</>
		);

	const meta = hasMeta ? (
		<div
			className={["as-audience-header__meta", editing ? "as-audience-header__meta--edit" : ""]
				.join(" ")
				.trim()}
		>
			<span className="as-audience-header__meta-item">
				<span className="as-audience-header__meta-label">ID</span>
				<span className="as-audience-header__meta-value">{id}</span>
				<button
					type="button"
					className="as-audience-header__copy"
					onClick={copyId}
					title="Copy ID"
				>
					<img
						src={copyIcon}
						alt="Copy"
					/>
				</button>
			</span>
			{destinationsItem ? (
				<>
					<span className="as-audience-header__meta-divider">|</span>
					<span className="as-audience-header__meta-item">{destinationsItem}</span>
				</>
			) : null}
			<span className="as-audience-header__meta-divider">|</span>
			<span className="as-audience-header__meta-item as-audience-header__meta-item--description">
				{editing && descriptionEditable ? (
					<input
						type="text"
						name="description"
						className="as-audience-header__description-inline"
						placeholder={descriptionPh}
						value={description ?? ""}
						onChange={(e) => onDescriptionChange(e.target.value)}
					/>
				) : (
					description || "No description"
				)}
			</span>
		</div>
	) : null;

	return (
		<div
			className={[
				"top-fold as-audience-header",
				compact ? "as-audience-header--compact" : "",
				className ?? "",
			]
				.join(" ")
				.trim()}
		>
			<div className="container">
				<div className="container--inner">
					{leading}
					<div className="as-audience-header__row">
						<div className="as-audience-header__left">
							{editing ? (
								<input
									type="text"
									name="name"
									className={[
										"as-audience-header__name-input",
										nameError ? "validation-error" : "",
									]
										.join(" ")
										.trim()}
									placeholder={namePh}
									value={name ?? ""}
									onChange={(e) => onNameChange(e.target.value)}
								/>
							) : (
								<h1 className="as-audience-header__name">{name || namePh}</h1>
							)}
							{meta}
							{editing && !hasMeta && descriptionEditable ? (
								<input
									type="text"
									name="description"
									className="as-audience-header__description-input"
									placeholder={descriptionPh}
									value={description ?? ""}
									onChange={(e) => onDescriptionChange(e.target.value)}
								/>
							) : null}
						</div>
						<div
							className={[
								"as-audience-header__actions",
								editing ? "" : "as-audience-header__actions--view",
							]
								.join(" ")
								.trim()}
						>
							{Array.isArray(menu) && menu.length > 0 ? (
								<div className="as-audience-header__menu">
									<button
										type="button"
										className="as-audience-header__menu-btn"
										onClick={() => setMenuOpen((open) => !open)}
										title="Actions"
										aria-haspopup="menu"
										aria-expanded={menuOpen}
									>
										<svg
											width="24"
											height="24"
											viewBox="0 0 24 24"
											fill="none"
											xmlns="http://www.w3.org/2000/svg"
											aria-hidden="true"
										>
											<circle cx="12" cy="5" r="2" fill="currentColor" />
											<circle cx="12" cy="12" r="2" fill="currentColor" />
											<circle cx="12" cy="19" r="2" fill="currentColor" />
										</svg>
									</button>
									{menuOpen ? (
										<div
											className="as-audience-header__menu-dropdown"
											role="menu"
										>
											{menu.map((item) => (
												<button
													key={item.label}
													type="button"
													role="menuitem"
													onClick={() => {
														setMenuOpen(false);
														if (item.onClick) item.onClick();
													}}
												>
													{item.label}
												</button>
											))}
										</div>
									) : null}
								</div>
							) : null}
							{error ? <div className="as-audience-header__error">{error}</div> : null}
							{actions}
						</div>
					</div>
				</div>
			</div>
		</div>
	);
}

AudienceHeader.propTypes = {
	name: PropTypes.string,
	/** present = the name is editable (edit / create); absent = read-only h1 (view) */
	onNameChange: PropTypes.func,
	/** default "Audience Name"; also the read-only fallback when the name is empty */
	namePlaceholder: PropTypes.string,
	/** underline the name input in the error colour */
	nameError: PropTypes.bool,
	description: PropTypes.string,
	/** present = editable: inline in the meta row (edit) or its own line (create) */
	onDescriptionChange: PropTypes.func,
	descriptionPlaceholder: PropTypes.string,
	/** the audience's unique id; present = the meta row renders (view / edit) */
	id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
	/** default: copy `id` to the clipboard */
	onCopyId: PropTypes.func,
	/** number of destinations the audience is pushed to; omit to hide the status */
	destinationsCount: PropTypes.number,
	/** present = the count / "Configure" is a link that opens the destinations editor */
	onConfigureDestinations: PropTypes.func,
	/** overflow menu (three dots) items, e.g. Copy / Delete / Export / Archive */
	menu: PropTypes.arrayOf(
		PropTypes.shape({ label: PropTypes.string.isRequired, onClick: PropTypes.func })
	),
	/** validation message rendered before the actions */
	error: PropTypes.node,
	/** the page's buttons */
	actions: PropTypes.node,
	/** content above the name row, inside the band (V2's "Back to suggestions") */
	leading: PropTypes.node,
	/** the sticky, scrolled-down state: single line, no meta */
	compact: PropTypes.bool,
	className: PropTypes.string,
};

export default AudienceHeader;
